import React, { useState, useEffect } from 'react';
import { 
  Activity, 
  AlertTriangle, 
  CheckCircle, 
  Clock, 
  Database, 
  FileText, 
  RefreshCw,
  TrendingUp,
  TrendingDown,
  AlertCircle,
  Loader
} from 'lucide-react';
import batchManagementService from '../../../services/batchManagementService';
import { toArray } from '../../../utils/normalize';

export default function UploadHealthDashboard() {
  const [healthData, setHealthData] = useState(null);
  const [recentActivity, setRecentActivity] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [lastUpdated, setLastUpdated] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);

  useEffect(() => {
    loadHealthData();
  }, []);

  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      loadHealthData(true); // Silent refresh
    }, 15000); // Refresh every 15 seconds

    return () => clearInterval(interval);
  }, [autoRefresh]);

  const loadHealthData = async (silent = false) => {
    if (!silent) setIsLoading(true);
    setError('');

    try {
      const [healthResult, activityResult] = await Promise.all([
        batchManagementService?.getProcessingHealthSummary(),
        batchManagementService?.getRecentActivity(24)
      ]);

      if (healthResult?.success) {
        setHealthData(healthResult?.data);
      } else {
        throw new Error(healthResult?.error || 'Failed to get health data');
      }

      if (activityResult?.success) {
        // Use safe array handling to prevent crashes
        setRecentActivity(toArray(activityResult?.data));
      }

      setLastUpdated(new Date());
    } catch (err) {
      setError('Failed to load health data: ' + err?.message);
    } finally {
      if (!silent) setIsLoading(false);
    }
  };

  const getHealthColor = (status) => {
    switch (status) {
      case 'healthy': return 'green';
      case 'warning': return 'yellow';
      case 'critical': return 'red';
      default: return 'gray';
    }
  };

  const getHealthIcon = (status) => {
    switch (status) {
      case 'healthy': return CheckCircle;
      case 'warning': return AlertTriangle;
      case 'critical': return AlertCircle;
      default: return Activity;
    }
  };

  const formatDuration = (startDate, endDate) => {
    if (!startDate || !endDate) return '-';
    
    const duration = new Date(endDate) - new Date(startDate);
    const minutes = Math.floor(duration / 60000);
    const seconds = Math.floor((duration % 60000) / 1000);
    
    if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    }
    return `${seconds}s`;
  };

  const getStatusBadgeColor = (status) => {
    const colors = {
      'completed': 'bg-green-100 text-green-800',
      'processing': 'bg-blue-100 text-blue-800',
      'queued': 'bg-yellow-100 text-yellow-800',
      'failed': 'bg-red-100 text-red-800',
      'stuck': 'bg-orange-100 text-orange-800'
    };
    return colors?.[status] || 'bg-gray-100 text-gray-800';
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-8">
        <div className="flex items-center justify-center">
          <Loader className="w-6 h-6 animate-spin text-gray-400 mr-2" />
          <span className="text-gray-600">Loading health dashboard...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-red-600" />
            <span className="text-red-700 text-sm">{error}</span>
          </div>
        </div>
      )}
      {/* Health Overview */}
      {healthData && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-gray-900">Upload Health Dashboard</h2>
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 text-sm text-gray-600">
                <input
                  type="checkbox"
                  checked={autoRefresh}
                  onChange={(e) => setAutoRefresh(e?.target?.checked)}
                  className="rounded"
                />
                Auto-refresh
              </label>
              <button
                onClick={() => loadHealthData()}
                className="p-2 text-gray-500 hover:text-gray-700 rounded-lg hover:bg-gray-100"
                title="Refresh data"
              >
                <RefreshCw className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Overall Health Status */}
          <div className="flex items-center gap-4 mb-6">
            {React.createElement(getHealthIcon(healthData?.status), {
              className: `w-8 h-8 text-${getHealthColor(healthData?.status)}-600`
            })}
            <div>
              <div className="text-2xl font-bold text-gray-900">
                System {healthData?.status?.charAt(0)?.toUpperCase() + healthData?.status?.slice(1)}
              </div>
              <div className="text-sm text-gray-600">
                Health Score: {healthData?.healthScore}% 
                {lastUpdated && (
                  <span className="ml-2">• Last updated: {lastUpdated?.toLocaleTimeString()}</span>
                )}
              </div>
            </div>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-6">
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <Database className="w-4 h-4 text-gray-600" />
                <span className="text-sm text-gray-600">Total</span>
              </div>
              <div className="text-2xl font-bold text-gray-900">{healthData?.totalBatches}</div>
            </div>

            <div className="bg-yellow-50 p-4 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <Clock className="w-4 h-4 text-yellow-600" />
                <span className="text-sm text-yellow-600">Queued</span>
              </div>
              <div className="text-2xl font-bold text-yellow-900">{healthData?.queuedBatches}</div>
            </div>

            <div className="bg-blue-50 p-4 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <Activity className="w-4 h-4 text-blue-600" />
                <span className="text-sm text-blue-600">Processing</span>
              </div>
              <div className="text-2xl font-bold text-blue-900">{healthData?.processingBatches}</div>
            </div>

            <div className="bg-red-50 p-4 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="w-4 h-4 text-red-600" />
                <span className="text-sm text-red-600">Stuck</span>
              </div>
              <div className="text-2xl font-bold text-red-900">{healthData?.stuckBatches}</div>
            </div>

            <div className="bg-green-50 p-4 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle className="w-4 h-4 text-green-600" />
                <span className="text-sm text-green-600">Completed</span>
              </div>
              <div className="text-2xl font-bold text-green-900">{healthData?.completedBatches}</div>
            </div>

            <div className="bg-orange-50 p-4 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <FileText className="w-4 h-4 text-orange-600" />
                <span className="text-sm text-orange-600">Failed Rows</span>
              </div>
              <div className="text-2xl font-bold text-orange-900">{healthData?.totalDeadletterRows}</div>
            </div>
          </div>

          {/* Recent Failures Alert */}
          {healthData?.recentFailures?.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="w-5 h-5 text-red-600" />
                <span className="font-medium text-red-800">
                  {healthData?.recentFailures?.length} Recent Failures (Last 24h)
                </span>
              </div>
              <div className="space-y-2">
                {healthData?.recentFailures?.slice(0, 3)?.map((failure, index) => (
                  <div key={index} className="text-sm text-red-700">
                    <span className="font-medium">{failure?.filename}</span>
                    {failure?.error_text && (
                      <span className="ml-2">- {failure?.error_text}</span>
                    )}
                  </div>
                ))}
                {healthData?.recentFailures?.length > 3 && (
                  <div className="text-sm text-red-600">
                    ... and {healthData?.recentFailures?.length - 3} more failures
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
      {/* Recent Activity */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Recent Activity (24h)</h3>
        
        {toArray(recentActivity)?.length === 0 ? (
          <div className="text-gray-500 text-center py-8">
            No recent upload activity
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Filename
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Started
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Duration
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Rows
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Success Rate
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Requeues
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {toArray(recentActivity)?.map((batch, index) => {
                  const successRate = batch?.rows_in_file > 0 ? 
                    ((batch?.rows_inserted / batch?.rows_in_file) * 100)?.toFixed(1) : '0.0';
                  
                  return (
                    <tr key={batch?.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {batch?.filename || 'Unknown'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadgeColor(batch?.status)}`}>
                          {batch?.status || 'unknown'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {batch?.upload_date ? new Date(batch.upload_date)?.toLocaleString() : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDuration(batch?.upload_date, batch?.completed_at)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {batch?.rows_inserted || 0} / {batch?.rows_in_file || 0}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <div className="flex items-center gap-1">
                          {successRate}%
                          {parseFloat(successRate) >= 95 ? (
                            <TrendingUp className="w-3 h-3 text-green-600" />
                          ) : parseFloat(successRate) < 50 ? (
                            <TrendingDown className="w-3 h-3 text-red-600" />
                          ) : null}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {batch?.requeue_count || 0}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}